import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import { ChefHat, Search, X, Info, ChevronDown } from 'lucide-react';
import { formatCurrency } from '../utils/currency';
import { getTranslation, searchTranslations } from '../utils/translations';
import CookieBanner from '../components/CookieBanner';
import RestaurantDetails from '../components/RestaurantDetails';
import { usePublicTranslation } from '../hooks/usePublicTranslation';
import { getTemplateById, applyTemplate } from '../utils/menuTemplates';
import { PublicMenuSEO } from '../components/SEO';
import { useMenuAnalytics } from '../utils/menuAnalytics';

const LANGUAGE_FLAGS = {
  en: '🇬🇧',
  tr: '🇹🇷',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
  it: '🇮🇹',
  ar: '🇸🇦',
  ru: '🇷🇺',
  zh: '🇨🇳',
  ja: '🇯🇵',
};

// Intelligent API base URL resolution
const getBackendUrl = () => {
  // 1. Use environment variable if set
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // 2. Use current domain (for production deployed on same domain)
  const protocol = window.location.protocol;
  const host = window.location.host;
  return `${protocol}//${host}`;
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const PublicMenu = () => {
  const { tenantId } = useParams(); // For route-based tenant: /menu/:tenantSlug
  const location = useLocation();
  const [menuData, setMenuData] = useState([]);
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [restaurantName, setRestaurantName] = useState(null);
  const [tenantSettings, setTenantSettings] = useState(null);
  const [showRestaurantDetails, setShowRestaurantDetails] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [showMenuItemDetail, setShowMenuItemDetail] = useState(false);
  const [modifierGroups, setModifierGroups] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [resolvedTenantId, setResolvedTenantId] = useState(null);
  
  // Refs for category navigation
  const categoryNavRef = useRef(null);
  const categoryRefs = useRef({});
  const langDropdownRef = useRef(null);
  
  // Translation hook
  const { t } = usePublicTranslation(language);
  
  // Analytics hook - uses resolved tenant ID
  const analytics = useMenuAnalytics(resolvedTenantId);

  // Apply template from tenant settings
  const applyTemplateFromSettings = (settings) => {
    // Get template ID (default to clean-list for new/migrating tenants)
    const templateId = settings?.template_id || 'clean-list';
    const template = getTemplateById(templateId);
    
    if (!template) {
      console.warn('Template not found:', templateId);
      applyTemplate(getTemplateById('clean-list'));
      return;
    }
    
    // Build custom colors object (for PRO users who customized)
    const customColors = {};
    if (settings?.custom_primary_color) {
      customColors.primary = settings.custom_primary_color;
    }
    if (settings?.custom_secondary_color) {
      customColors.secondary = settings.custom_secondary_color;
    }
    
    // Apply template with any custom colors
    applyTemplate(template, customColors);
  };

  // Get tenant slug from URL params or query string (required)
  const getTenantSlug = () => {
    if (tenantId) return tenantId; // /menu/:tenantSlug
    const params = new URLSearchParams(location.search);
    const tenantParam = params.get('tenant');
    if (tenantParam) return tenantParam;
    return null; // slug is required
  };

  const getInitialLanguage = (defaultLang = 'tr') => {
    // Check if language is saved in cookie
    const savedLang = Cookies.get('menu_language');
    if (savedLang && (savedLang === 'en' || savedLang === 'tr')) {
      return savedLang;
    }

    // Detect browser language
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang) {
      const langCode = browserLang.toLowerCase().split('-')[0];
      if (langCode === 'tr') return 'tr';
      if (langCode === 'en') return 'en';
    }

    // Fall back to default language from config
    return defaultLang;
  };

  useEffect(() => {
    fetchData();
  }, [location.search, tenantId]); // Refetch when tenant changes
  
  // Track menu page view when tenant is resolved
  useEffect(() => {
    if (resolvedTenantId && !loading) {
      console.log('[Analytics] Tracking menu view for tenant:', resolvedTenantId);
      analytics.trackMenuView();
    }
  }, [resolvedTenantId, loading]);

  // Update page title with restaurant name
  useEffect(() => {
    if (restaurantName) {
      document.title = `${restaurantName} - Menu - idemenu`;
    } else {
      document.title = 'Menu - idemenu';
    }
  }, [restaurantName]);

  // Note: Google Analytics is loaded globally in App.js from database config

  const fetchData = async () => {
    if (!API) {
      setError('Backend URL is not configured');
      setLoading(false);
      return;
    }

    try {
      const tenantSlug = getTenantSlug();
      if (!tenantSlug) {
        setError('Tenant slug is required to view this menu');
        setLoading(false);
        return;
      }
      
      // Fetch menu with tenant slug parameter
      const menuRes = await axios.get(`${API}/public/menu`, {
        params: { tenant: tenantSlug }
      });
      
      // Try to fetch config (may fail for new tenants without config)
      let configData = null;
      try {
        const configRes = await axios.get(`${API}/config`);
        configData = configRes.data;
      } catch (err) {
        console.warn('Config not available, using defaults');
      }

      // Fetch tenant info to get logo_url and other tenant-specific data
      let tenantInfo = null;
      const tenantId = menuRes.data.tenant_id;
      if (tenantId) {
        try {
          const tenantRes = await axios.get(`${API}/tenants/${tenantId}/info`);
          tenantInfo = tenantRes.data;
          // Merge tenant logo into config for backward compatibility
          if (configData && tenantInfo.logo_url) {
            configData.logo_url = tenantInfo.logo_url;
          } else if (!configData) {
            configData = { logo_url: tenantInfo.logo_url };
          }
        } catch (err) {
          console.warn('Tenant info not available');
        }
      }

      setMenuData(menuRes.data.categories);
      setModifierGroups(menuRes.data.modifier_groups || []);
      setConfig(configData);
      
      // Store tenant ID for analytics
      const apiTenantId = menuRes.data.tenant_id;
      console.log('[Analytics] Tenant ID from API:', apiTenantId);
      if (apiTenantId) {
        setResolvedTenantId(apiTenantId);
      }
      
      // Set initial active category to the first one
      if (menuRes.data.categories.length > 0) {
        setActiveCategory(menuRes.data.categories[0].category.id);
      }
      
      // Store tenant settings for restaurant details modal and apply template
      if (tenantInfo?.settings) {
        setTenantSettings(tenantInfo.settings);
        
        // Check for preview query params (from admin customization preview)
        const searchParams = new URLSearchParams(location.search);
        const previewTemplate = searchParams.get('preview_template');
        
        if (previewTemplate) {
          // Override with preview params for live preview of unsaved changes
          const previewSettings = {
            ...tenantInfo.settings,
            template_id: previewTemplate,
          };
          const previewPrimary = searchParams.get('preview_primary');
          const previewSecondary = searchParams.get('preview_secondary');
          if (previewPrimary) previewSettings.custom_primary_color = previewPrimary;
          if (previewSecondary) previewSettings.custom_secondary_color = previewSecondary;
          applyTemplateFromSettings(previewSettings);
        } else {
          applyTemplateFromSettings(tenantInfo.settings);
        }
      } else {
        // Default template for tenants without settings
        applyTemplate(getTemplateById('clean-list'));
      }
      
      // Set restaurant name from tenant settings or format tenant slug as fallback
      const restaurantNameFromData = tenantInfo?.settings?.restaurant_name || tenantSlug.replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase());
      setRestaurantName(restaurantNameFromData);
      
      // Set initial language based on cookie, browser, or tenant settings/config
      const defaultLang = tenantInfo?.settings?.default_language || configData?.default_language || 'tr';
      let initialLang = getInitialLanguage(defaultLang);
      
      // Ensure the initial language is supported
      const supportedLangs = tenantInfo?.settings?.supported_languages || configData?.supported_languages || ['tr'];
      if (!supportedLangs.includes(initialLang)) {
        initialLang = supportedLangs[0] || 'tr';
      }
      
      setLanguage(initialLang);
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch menu:', err);
      setError(err.response?.data?.detail || 'Failed to load menu');
      setLoading(false);
    }
  };

  // Check if tenant has any business info to display
  const hasBusinessInfo = () => {
    if (!tenantSettings) return false;
    const { address, website, telephone, gsm_number, location_url, instagram_url, twitter_url, facebook_url } = tenantSettings;
    return !!(address || website || telephone || gsm_number || location_url || instagram_url || twitter_url || facebook_url);
  };

  const changeLanguage = (newLang) => {
    setLanguage(newLang);
    // Save to cookie (expires in 365 days)
    Cookies.set('menu_language', newLang, { expires: 365 });
    setLangDropdownOpen(false);
  };

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Track active category based on scroll position
  useEffect(() => {
    if (menuData.length === 0 || searchQuery) return;

    const observerOptions = {
      root: null,
      rootMargin: '-200px 0px -60% 0px', // Adjust for sticky headers
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const categoryId = entry.target.id.replace('category-', '');
          setActiveCategory(categoryId);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all category sections
    menuData.forEach((categoryData) => {
      const element = document.getElementById(`category-${categoryData.category.id}`);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [menuData, searchQuery]);

  // Auto-scroll category nav to show active category
  useEffect(() => {
    if (!activeCategory || !categoryNavRef.current) return;

    const activeNavItem = categoryRefs.current[activeCategory];
    if (activeNavItem) {
      const nav = categoryNavRef.current;
      const navRect = nav.getBoundingClientRect();
      const itemRect = activeNavItem.getBoundingClientRect();

      // Check if item is out of view
      if (itemRect.left < navRect.left || itemRect.right > navRect.right) {
        // Scroll to center the active item
        const scrollLeft = activeNavItem.offsetLeft - (nav.offsetWidth / 2) + (activeNavItem.offsetWidth / 2);
        nav.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [activeCategory]);

  // Helper to check if item has a size-type modifier (single required)
  const hasSizeTypeModifier = (item) => {
    if (!item.modifier_group_ids || item.modifier_group_ids.length === 0) return false;
    return item.modifier_group_ids.some(groupId => {
      const group = modifierGroups.find(g => g.id === groupId);
      return group && group.selection_type === 'single' && group.min_selections > 0;
    });
  };

  // Helper to render inline modifier groups for a menu item
  const renderInlineModifiers = (item) => {
    if (!item.modifier_group_ids || item.modifier_group_ids.length === 0) return null;
    
    const itemModifierGroups = item.modifier_group_ids
      .map(groupId => modifierGroups.find(g => g.id === groupId))
      .filter(Boolean);
    
    if (itemModifierGroups.length === 0) return null;
    
    const basePrice = item.base_price || 0;
    
    return (
      <div className="inline-modifiers">
        {itemModifierGroups.map(group => {
          const availableOptions = (group.options || []).filter(opt => opt.available !== false);
          if (availableOptions.length === 0) return null;
          
          // For single-selection required groups (like Size), show final prices without group name
          const isSizeType = group.selection_type === 'single' && group.min_selections > 0;
          
          return (
            <div key={group.id} className={`inline-modifier-group ${isSizeType ? 'size-type' : ''}`}>
              {/* Only show group name for non-size-type modifiers */}
              {!isSizeType && (
                <span className="inline-group-name">
                  {getTranslation(group.translations, language, 'name')}
                  {group.min_selections > 0 && <span className="inline-required">*</span>}
                  :
                </span>
              )}
              <span className="inline-options">
                {availableOptions.slice(0, 4).map((opt, idx) => {
                  const finalPrice = basePrice + (opt.price_adjustment || 0);
                  
                  return (
                    <span key={opt.id} className="inline-option">
                      {getTranslation(opt.translations, language, 'name')}
                      {isSizeType ? (
                        // Show final price for size-type options
                        <span className="inline-final-price">
                          {formatCurrency(finalPrice)}
                        </span>
                      ) : (
                        // Show price adjustment for add-on type options
                        opt.price_adjustment !== 0 && (
                          <span className={`inline-price ${opt.price_adjustment > 0 ? 'positive' : 'negative'}`}>
                            {opt.price_adjustment > 0 ? '+' : ''}{formatCurrency(opt.price_adjustment)}
                          </span>
                        )
                      )}
                      {idx < Math.min(availableOptions.length, 4) - 1 && ' · '}
                    </span>
                  );
                })}
                {availableOptions.length > 4 && (
                  <span className="inline-more">+{availableOptions.length - 4}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const clearSearch = () => {
    setSearchQuery('');
  };
  
  // Debounced search tracking
  const searchTimeoutRef = useRef(null);
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Track search after 500ms of inactivity
    if (query.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        analytics.trackSearch(query);
      }, 500);
    }
  };

  const filterMenuData = () => {
    if (!searchQuery.trim()) {
      return menuData;
    }

    const query = searchQuery.toLowerCase();
    return menuData.map(categoryData => {
      if (categoryData.is_parent && categoryData.subcategories) {
        // For parent categories, filter items within each subcategory
        const filteredSubcategories = categoryData.subcategories.map(subData => ({
          ...subData,
          items: subData.items.filter(item => 
            searchTranslations(item.translations, query, ['name', 'description'])
          )
        })).filter(subData => subData.items.length > 0);
        
        return {
          ...categoryData,
          subcategories: filteredSubcategories
        };
      } else {
        // For leaf categories, filter items directly
        const filteredItems = categoryData.items.filter(item => {
          return searchTranslations(item.translations, query, ['name', 'description']);
        });

        return {
          ...categoryData,
          items: filteredItems
        };
      }
    }).filter(categoryData => {
      if (categoryData.is_parent) {
        return categoryData.subcategories && categoryData.subcategories.length > 0;
      }
      return categoryData.items.length > 0;
    });
  };

  const filteredData = filterMenuData();

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: '#f5f5f5',
        padding: '20px'
      }}>
        <div style={{ 
          textAlign: 'center',
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          maxWidth: '500px'
        }}>
          <h2 style={{ color: '#d32f2f', marginTop: 0 }}>⚠️ Error</h2>
          <p style={{ color: '#666', fontSize: '16px' }}>{error}</p>
          <p style={{ color: '#999', fontSize: '14px' }}>
            BACKEND_URL: {BACKEND_URL || 'Not set'}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('publicMenu.loadingMenu')}</p>
        <style>{`
          .loading-container {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, var(--color-secondary, #667eea) 0%, var(--color-dark, #764ba2) 100%);
            color: white;
          }
          .loading-spinner {
            width: 50px;
            height: 50px;
            border: 4px solid rgba(255, 255, 255, 0.3);
            border-top-color: white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
    <PublicMenuSEO 
      restaurantName={restaurantName}
      slug={tenantId}
      description={tenantSettings?.description}
      logo={config?.logo_url ? `${BACKEND_URL}${config.logo_url}` : null}
      categories={menuData}
    />
    <div className="public-menu">
      {/* Fixed Top Header */}
      <header className="menu-header">
        <div className="header-content">
          <div className="brand">
            <h1 data-testid="restaurant-name">{restaurantName || t('publicMenu.restaurantMenu')}</h1>
          </div>
          <div className="header-actions">
            {/* Only show info button if there's actual business info */}
            {hasBusinessInfo() && (
              <button 
                className="info-button" 
                onClick={() => setShowRestaurantDetails(true)}
                aria-label={t('publicMenu.restaurantInfo')}
                title={t('publicMenu.restaurantInfo')}
                data-testid="restaurant-info-btn"
              >
                <Info size={18} />
              </button>
            )}
            {(config?.supported_languages || ['tr']).length > 1 && (
              <div className="lang-dropdown" ref={langDropdownRef}>
                <button 
                  className="lang-toggle" 
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)} 
                  data-testid="public-lang-toggle"
                >
                  <span className="lang-flag">{LANGUAGE_FLAGS[language] || '🌐'}</span>
                  <span>{language.toUpperCase()}</span>
                  <ChevronDown size={14} className={`lang-chevron ${langDropdownOpen ? 'open' : ''}`} />
                </button>
                {langDropdownOpen && (
                  <div className="lang-dropdown-menu">
                    {(config?.supported_languages || ['tr']).map(langCode => (
                      <button
                        key={langCode}
                        className={`lang-option ${langCode === language ? 'active' : ''}`}
                        onClick={() => changeLanguage(langCode)}
                      >
                        <span className="lang-flag">{LANGUAGE_FLAGS[langCode] || '🌐'}</span>
                        <span>{langCode.toUpperCase()}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Restaurant Logo Section */}
      {config?.logo_url && (
        <div className="logo-section">
          <img 
            src={`${BACKEND_URL}${config.logo_url}`}
            alt={restaurantName}
            className="restaurant-logo"
            data-testid="main-logo"
            onError={(e) => e.target.parentElement.style.display = 'none'}
          />
        </div>
      )}

      {/* Search Box */}
      <div className="search-section">
        <div className="search-container">
          <Search className="search-icon" size={20} />
          <input
            type="text"
            placeholder={t('publicMenu.searchPlaceholder')}
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
            data-testid="menu-search-input"
          />
          {searchQuery && (
            <button 
              onClick={clearSearch} 
              className="search-clear"
              data-testid="clear-search-btn"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Sticky Category Navigation */}
      {menuData.length > 0 && !searchQuery && (
        <nav className="category-nav" ref={categoryNavRef}>
          <div className="category-nav-scroll">
            {menuData.map((categoryData, idx) => (
              <a
                key={idx}
                ref={(el) => { categoryRefs.current[categoryData.category.id] = el; }}
                href={`#category-${categoryData.category.id}`}
                className={`category-nav-item ${categoryData.is_parent ? 'parent-nav-item' : ''} ${activeCategory === categoryData.category.id ? 'active' : ''}`}
                data-testid={`nav-${categoryData.category.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  // Track category view
                  analytics.trackCategoryView(categoryData.category.id);
                  setActiveCategory(categoryData.category.id);
                  const element = document.getElementById(`category-${categoryData.category.id}`);
                  if (element) {
                    // Calculate offset for sticky headers (header + search + nav)
                    const offset = 185; // Total height of sticky elements
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - offset;
                    
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }}
              >
                {getTranslation(categoryData.category.translations, language, 'name')}
              </a>
            ))}
          </div>
        </nav>
      )}
      {/* Cookie Banner */}
      <CookieBanner context="menu" language={language} />

      {/* Menu Content */}
      <main className="menu-content">
        {searchQuery && filteredData.length === 0 ? (
          <div className="empty-menu">
            <Search size={48} color="#ccc" />
            <p>{t('publicMenu.noItemsFound')}</p>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="empty-menu">
            <p>{t('publicMenu.noMenuItems')}</p>
          </div>
        ) : (
          filteredData.map((categoryData, idx) => (
            <section 
              key={idx} 
              id={`category-${categoryData.category.id}`}
              className={`category-section ${categoryData.is_parent ? 'parent-category-section' : ''}`}
              data-testid={`category-section-${categoryData.category.id}`}
            >
              <h2 className={`category-title ${categoryData.is_parent ? 'parent-category-title' : ''}`}>
                {getTranslation(categoryData.category.translations, language, 'name')}
                {searchQuery && !categoryData.is_parent && (
                  <span className="result-count"> ({categoryData.items.length})</span>
                )}
              </h2>
              
              {/* For parent categories, render subcategories */}
              {categoryData.is_parent && categoryData.subcategories && categoryData.subcategories.map((subData, subIdx) => (
                <div 
                  key={subIdx} 
                  className="subcategory-section"
                  id={`subcategory-${subData.category.id}`}
                >
                  <h3 className="subcategory-title">
                    {getTranslation(subData.category.translations, language, 'name')}
                    {searchQuery && (
                      <span className="result-count"> ({subData.items.length})</span>
                    )}
                  </h3>
                  <div className="items-list">
                    {subData.items.map((item) => (
                      <div 
                        key={item.id} 
                        className={`menu-item-card ${tenantSettings?.layout_mode || 'left'}`}
                        data-testid={`menu-item-${item.id}`}
                        onClick={() => {
                          // Track item view
                          analytics.trackItemView(item.id);
                          if (tenantSettings?.enable_item_details) {
                            setSelectedMenuItem(item);
                            setShowMenuItemDetail(true);
                          }
                        }}
                        style={{
                          cursor: tenantSettings?.enable_item_details ? 'pointer' : 'default',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          transform: tenantSettings?.enable_item_details ? 'none' : 'none'
                        }}
                      >
                        {item.image_url && (tenantSettings?.layout_mode !== 'no-image') && (
                          <div className={`item-image item-image-${tenantSettings?.layout_mode || 'left'}`}>
                            <img src={item.image_url} alt={getTranslation(item.translations, language, 'name')} />
                          </div>
                        )}
                        <div className="item-content">
                          <div className="item-header">
                            <h3 className="item-name" data-testid="item-name">
                              {getTranslation(item.translations, language, 'name')}
                            </h3>
                            {/* Hide base price if item has size-type modifier (prices shown in options) */}
                            {!hasSizeTypeModifier(item) && item?.base_price !== null && item?.base_price !== undefined && Number.isFinite(Number(item.base_price)) && (
                              <span className="item-price" data-testid="item-price">
                                {formatCurrency(item.base_price)}
                              </span>
                            )}
                          </div>
                          {getTranslation(item.translations, language, 'description') && (
                            <p className="item-description" data-testid="item-description">
                              {getTranslation(item.translations, language, 'description')}
                            </p>
                          )}
                          {renderInlineModifiers(item)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {/* For leaf categories (no subcategories), render items directly */}
              {!categoryData.is_parent && (
                <div className="items-list">
                  {categoryData.items.map((item) => (
                    <div 
                      key={item.id} 
                      className={`menu-item-card ${tenantSettings?.layout_mode || 'left'}`}
                      data-testid={`menu-item-${item.id}`}
                      onClick={() => {
                        // Track item view
                        analytics.trackItemView(item.id);
                        if (tenantSettings?.enable_item_details) {
                          setSelectedMenuItem(item);
                          setShowMenuItemDetail(true);
                        }
                      }}
                      style={{
                        cursor: tenantSettings?.enable_item_details ? 'pointer' : 'default',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        transform: tenantSettings?.enable_item_details ? 'none' : 'none'
                      }}
                    >
                      {item.image_url && (tenantSettings?.layout_mode !== 'no-image') && (
                        <div className={`item-image item-image-${tenantSettings?.layout_mode || 'left'}`}>
                          <img src={item.image_url} alt={getTranslation(item.translations, language, 'name')} />
                        </div>
                      )}
                      <div className="item-content">
                        <div className="item-header">
                          <h3 className="item-name" data-testid="item-name">
                            {getTranslation(item.translations, language, 'name')}
                          </h3>
                          {/* Hide base price if item has size-type modifier (prices shown in options) */}
                          {!hasSizeTypeModifier(item) && item?.base_price !== null && item?.base_price !== undefined && Number.isFinite(Number(item.base_price)) && (
                            <span className="item-price" data-testid="item-price">
                              {formatCurrency(item.base_price)}
                            </span>
                          )}
                        </div>
                        {getTranslation(item.translations, language, 'description') && (
                          <p className="item-description" data-testid="item-description">
                            {getTranslation(item.translations, language, 'description')}
                          </p>
                        )}
                        {renderInlineModifiers(item)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))
        )}
      </main>

      {/* Footer */}
      <footer className="menu-footer">
        <div className="footer-content">
          <a href="https://idemenu.com" target="_blank" rel="noopener noreferrer" className="footer-link">
            <img src="/idemenu-footer-logo.svg" alt="idemenu" className="footer-logo" />
          </a>
          <p className="footer-copyright">{t('footer.copyright', { year: new Date().getFullYear() })}</p>
        </div>
      </footer>

      {/* Restaurant Details Modal */}
      {showRestaurantDetails && (
        <RestaurantDetails 
          settings={tenantSettings}
          onClose={() => setShowRestaurantDetails(false)}
          language={language}
        />
      )}

      {/* Menu Item Detail Overlay */}
      {showMenuItemDetail && selectedMenuItem && (
        <div className="menu-item-overlay">
          <div className="overlay-backdrop" onClick={() => setShowMenuItemDetail(false)} />
          <div className="overlay-panel">
            <button 
              className="overlay-close"
              onClick={() => setShowMenuItemDetail(false)}
            >
              ✕
            </button>
            
            {selectedMenuItem.image_url && (
              <div className="overlay-image">
                <img 
                  src={selectedMenuItem.image_url} 
                  alt={getTranslation(selectedMenuItem.translations, language, 'name')}
                />
              </div>
            )}
            
            <div className="overlay-content">
              <h2 className="overlay-title">
                {getTranslation(selectedMenuItem.translations, language, 'name')}
              </h2>
              
              {/* Hide base price if item has size-type modifier (prices shown in options) */}
              {!hasSizeTypeModifier(selectedMenuItem) && selectedMenuItem?.base_price !== null && selectedMenuItem?.base_price !== undefined && Number.isFinite(Number(selectedMenuItem.base_price)) && (
                <p className="overlay-price">
                  {formatCurrency(selectedMenuItem.base_price)}
                </p>
              )}
              
              {getTranslation(selectedMenuItem.translations, language, 'description') && (
                <p className="overlay-description">
                  {getTranslation(selectedMenuItem.translations, language, 'description')}
                </p>
              )}
              
              {/* Modifier Groups */}
              {selectedMenuItem.modifier_group_ids && selectedMenuItem.modifier_group_ids.length > 0 && (
                <div className="overlay-modifiers">
                  {selectedMenuItem.modifier_group_ids.map(groupId => {
                    const group = modifierGroups.find(g => g.id === groupId);
                    if (!group) return null;
                    
                    const availableOptions = (group.options || []).filter(opt => opt.available !== false);
                    if (availableOptions.length === 0) return null;
                    
                    // For single-selection required groups (like Size), show final prices
                    const isSizeType = group.selection_type === 'single' && group.min_selections > 0;
                    const basePrice = selectedMenuItem.base_price || 0;
                    
                    return (
                      <div key={groupId} className={`modifier-group ${isSizeType ? 'size-type' : ''}`}>
                        {/* Hide header for size-type modifiers - just show options directly */}
                        {!isSizeType && (
                          <div className="modifier-group-header">
                            <h4 className="modifier-group-title">
                              {getTranslation(group.translations, language, 'name')}
                            </h4>
                            <span className="modifier-group-rule">
                              {group.min_selections > 0 ? (
                                <span className="required-tag">{t('publicMenu.required') || 'Required'}</span>
                              ) : (
                                <span className="optional-tag">{t('publicMenu.optional') || 'Optional'}</span>
                              )}
                              {group.selection_type === 'single' ? (
                                <span className="selection-info">{t('publicMenu.selectOne') || 'Select one'}</span>
                              ) : (
                                <span className="selection-info">
                                  {group.max_selections > 1 
                                    ? `${t('publicMenu.selectUpTo') || 'Select up to'} ${group.max_selections}`
                                    : t('publicMenu.selectAny') || 'Select any'}
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                        <div className="modifier-options">
                          {availableOptions.map((option) => {
                            const finalPrice = basePrice + (option.price_adjustment || 0);
                            
                            return (
                              <div key={option.id} className="modifier-option">
                                <span className="option-name">
                                  {getTranslation(option.translations, language, 'name')}
                                </span>
                                {isSizeType ? (
                                  // Show final price for size-type options
                                  <span className="option-final-price">
                                    {formatCurrency(finalPrice)}
                                  </span>
                                ) : (
                                  // Show price adjustment for add-on type options
                                  option.price_adjustment !== 0 && (
                                    <span className={`option-price ${option.price_adjustment > 0 ? 'positive' : 'negative'}`}>
                                      {option.price_adjustment > 0 ? '+' : ''}{formatCurrency(option.price_adjustment)}
                                    </span>
                                  )
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              <button 
                className="overlay-close-btn"
                onClick={() => setShowMenuItemDetail(false)}
              >
                {t('common.close') || 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .public-menu {
          /* Template-based CSS variables with fallbacks */
          --pm-primary: var(--template-primary, #FFC421);
          --pm-secondary: var(--template-secondary, #764ba2);
          --pm-accent: var(--template-accent, var(--pm-primary));
          --pm-background: var(--template-background, #f8f9fa);
          --pm-surface: var(--template-surface, #ffffff);
          --pm-text: var(--template-text, #1a1a1a);
          --pm-text-muted: var(--template-text-muted, #666);
          --pm-border: var(--template-border, #e5e5e5);
          --pm-header-bg: var(--template-header-bg, var(--pm-primary));
          --pm-header-text: var(--template-header-text, #ffffff);
          --pm-category-bg: var(--template-category-bg, transparent);
          --pm-category-text: var(--template-category-text, var(--pm-text));
          --pm-card-bg: var(--template-card-bg, #ffffff);
          --pm-price-badge: var(--template-price-badge, var(--pm-primary));
          
          /* Typography */
          --pm-heading-font: var(--template-typography-heading-font, 'Manrope'), sans-serif;
          --pm-body-font: var(--template-typography-body-font, 'Inter'), sans-serif;
          --pm-heading-weight: var(--template-typography-heading-weight, 700);
          --pm-body-weight: var(--template-typography-body-weight, 400);
          
          /* Spacing */
          --pm-card-radius: var(--template-spacing-card-radius, 12px);
          --pm-button-radius: var(--template-spacing-button-radius, 8px);
          
          min-height: 100vh;
          background: var(--pm-background);
          padding-bottom: 20px;
        }

        .menu-header {
          background: var(--pm-header-bg);
          color: var(--pm-header-text);
          padding: 16px 0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-content {
          max-width: 100%;
          margin: 0 auto;
          padding: 0 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .brand {
          display: flex;
          align-items: center;
        }

        .brand h1 {
          font-size: var(--template-typography-restaurant-name-size, 20px);
          font-weight: var(--pm-heading-weight);
          font-family: var(--pm-heading-font);
          margin: 0;
          color: var(--pm-header-text);
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .info-button {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(4px);
          border: 1.5px solid rgba(255, 255, 255, 0.25);
          border-radius: 50%;
          width: 36px;
          height: 36px;
          padding: 0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          color: var(--pm-header-text);
          flex-shrink: 0;
        }

        .info-button:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        .info-button:active {
          transform: translateY(0);
          background: rgba(255, 255, 255, 0.35);
        }

        .logo-section {
          background: var(--pm-surface);
          padding: 24px;
          display: flex;
          justify-content: center;
          align-items: center;
          border-bottom: 1px solid var(--pm-border);
        }

        .restaurant-logo {
          max-width: 200px;
          max-height: 120px;
          width: auto;
          height: auto;
          object-fit: contain;
        }

        .search-section {
          background: var(--pm-surface);
          padding: 16px;
          border-bottom: 1px solid var(--pm-border);
          position: sticky;
          top: 57px;
          z-index: 95;
        }

        .search-container {
          max-width: 600px;
          margin: 0 auto;
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          color: var(--pm-text-muted);
          pointer-events: none;
        }

        .search-input {
          width: 100%;
          padding: 12px 42px 12px 42px;
          border: 2px solid var(--pm-border);
          border-radius: 24px;
          font-size: 15px;
          transition: all 0.2s;
          background: var(--pm-background);
          color: var(--pm-text);
          font-family: var(--pm-body-font);
        }

        .search-input:focus {
          outline: none;
          border-color: var(--pm-primary);
          background: var(--pm-surface);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .search-input::placeholder {
          color: var(--pm-text-muted);
        }

        .search-clear {
          position: absolute;
          right: 12px;
          background: var(--pm-border);
          border: none;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s;
          color: #666;
        }

        .search-clear:hover {
          background: #d5d5d5;
        }

        .result-count {
          font-size: 18px;
          color: var(--pm-text-muted);
          font-weight: 400;
        }

        .lang-toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.95);
          border: none;
          border-radius: 20px;
          color: #1a1a1a;
          font-weight: 600;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
        }

        .lang-toggle:hover {
          background: #f8f9fa;
          transform: scale(1.05);
        }

        .lang-dropdown {
          position: relative;
        }

        .lang-flag {
          font-size: 16px;
          line-height: 1;
        }

        .lang-chevron {
          transition: transform 0.2s ease;
        }

        .lang-chevron.open {
          transform: rotate(180deg);
        }

        .lang-dropdown-menu {
          position: absolute;
          top: calc(100% + 4px);
          right: 0;
          min-width: 100px;
          background: white;
          border: 1px solid #e5e5e5;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          overflow: hidden;
        }

        .lang-option {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 14px;
          background: none;
          border: none;
          font-size: 14px;
          font-weight: 500;
          color: #1a1a1a;
          cursor: pointer;
          transition: background-color 0.15s;
          text-align: left;
        }

        .lang-option:hover {
          background: #f5f5f5;
        }

        .lang-option.active {
          background: var(--pm-primary-bg, #e0e7ff);
          color: var(--pm-primary, #4f46e5);
          font-weight: 600;
        }

        .category-nav {
          position: sticky;
          top: 130px;
          background: var(--pm-surface);
          border-bottom: 1px solid var(--pm-border);
          z-index: 90;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .category-nav::-webkit-scrollbar {
          display: none;
        }

        .category-nav-scroll {
          display: flex;
          padding: 16px 16px;
          gap: 8px;
          min-height: 48px;
          align-items: center;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .category-nav-item {
          flex-shrink: 0;
          padding: 8px 16px;
          background: var(--pm-background);
          border-radius: 20px;
          text-decoration: none;
          color: var(--pm-text);
          font-size: 14px;
          font-weight: 500;
          font-family: var(--pm-body-font);
          transition: all 0.25s ease;
          border: 2px solid transparent;
          position: relative;
        }

        .category-nav-item:hover {
          background: var(--pm-primary);
          color: var(--pm-header-text);
          border-color: var(--pm-primary);
        }

        /* Active category styling with overlay effect */
        .category-nav-item.active {
          background: var(--pm-primary);
          color: var(--pm-header-text);
          border-color: var(--pm-primary);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transform: scale(1.05);
        }

        .category-nav-item.active::after {
          content: '';
          position: absolute;
          inset: -3px;
          border-radius: 22px;
          background: var(--pm-primary);
          opacity: 0.2;
          z-index: -1;
          animation: pulseGlow 1.5s ease-in-out infinite;
        }

        @keyframes pulseGlow {
          0%, 100% {
            transform: scale(1);
            opacity: 0.2;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.1;
          }
        }

        .menu-content {
          padding: 16px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .category-section {
          margin-bottom: 32px;
          padding-top: 16px;
          padding-bottom: 16px;
        }
        
        .parent-category-section {
          margin-bottom: 40px;
        }

        .category-title {
          font-size: var(--template-typography-category-title-size, 24px);
          font-weight: var(--pm-heading-weight);
          color: var(--pm-category-text);
          background: var(--pm-category-bg);
          margin-bottom: 16px;
          padding: 12px 16px;
          font-family: var(--pm-heading-font);
          border-bottom: 3px solid var(--pm-primary);
          display: inline-block;
          border-radius: 4px;
        }
        
        .parent-category-title {
          font-size: var(--template-typography-category-title-size, 28px);
          background: linear-gradient(135deg, var(--pm-primary) 0%, var(--pm-secondary) 100%);
          color: white;
          border-bottom: none;
          border-radius: 8px;
          width: 100%;
          display: block;
          text-align: center;
        }
        
        .subcategory-section {
          margin-bottom: 24px;
          padding-left: 8px;
          border-left: 3px solid var(--pm-primary);
        }
        
        .subcategory-title {
          font-size: var(--template-typography-category-title-size, 20px);
          font-weight: var(--pm-heading-weight);
          color: var(--pm-primary);
          margin-bottom: 12px;
          padding: 8px 12px;
          font-family: var(--pm-heading-font);
        }

        .items-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .menu-item-card {
          background: var(--pm-card-bg);
          border-radius: var(--pm-card-radius);
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          transition: all 0.2s;
          display: flex;
          border: 1px solid var(--pm-border);
        }

        .menu-item-card:active {
          transform: scale(0.98);
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
        }

        .item-image {
          width: 100px;
          height: 100px;
          flex-shrink: 0;
          overflow: hidden;
          background: var(--pm-border);
        }

        .item-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .item-content {
          padding: 12px 16px;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .item-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 6px;
        }

        .item-name {
          font-size: var(--template-typography-item-name-size, 16px);
          font-weight: var(--pm-heading-weight);
          color: var(--pm-text);
          margin: 0;
          flex: 1;
          font-family: var(--pm-heading-font);
          line-height: 1.3;
        }

        .item-price {
          font-size: var(--template-typography-price-size, 16px);
          font-weight: 700;
          color: var(--pm-price-badge);
          white-space: nowrap;
          font-family: var(--pm-heading-font);
        }

        .item-description {
          font-size: var(--template-typography-item-desc-size, 13px);
          color: var(--pm-text-muted);
          line-height: 1.4;
          margin: 0;
          font-family: var(--pm-body-font);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* Inline Modifiers on Menu Item Cards */
        .inline-modifiers {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px dashed var(--pm-border);
        }

        .inline-modifier-group {
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          gap: 6px;
          font-size: 14px;
          line-height: 1.5;
        }

        .inline-group-name {
          font-weight: 600;
          color: var(--pm-text);
          font-family: var(--pm-body-font);
          font-size: 14px;
        }

        .inline-required {
          color: #dc2626;
          margin-left: 1px;
        }

        .inline-options {
          color: var(--pm-text-muted);
          font-family: var(--pm-body-font);
          font-size: 14px;
        }

        .inline-option {
          white-space: nowrap;
        }

        .inline-price {
          font-size: 13px;
          font-weight: 600;
          margin-left: 3px;
        }

        .inline-price.positive {
          color: #10b981;
        }

        .inline-price.negative {
          color: #ef4444;
        }

        .inline-final-price {
          font-size: 15px;
          font-weight: 700;
          color: var(--pm-price-badge);
          margin-left: 4px;
        }

        .inline-modifier-group.size-type {
          background: var(--pm-background);
          padding: 10px 14px;
          border-radius: 10px;
          margin-top: 6px;
          font-size: 15px;
        }

        .inline-modifier-group.size-type .inline-options {
          color: var(--pm-text);
          font-weight: 500;
          font-size: 15px;
        }

        .inline-modifier-group.size-type .inline-final-price {
          font-size: 16px;
        }

        .inline-more {
          background: var(--pm-background);
          padding: 1px 6px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 500;
          color: var(--pm-text-muted);
          margin-left: 4px;
        }

        .empty-menu {
          text-align: center;
          padding: 60px 20px;
          color: var(--pm-text-muted);
          font-size: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          font-family: var(--pm-body-font);
        }

        /* Layout Modes */
        .menu-item-card.left {
          flex-direction: row;
        }

        .menu-item-card.right {
          flex-direction: row-reverse;
        }

        .menu-item-card.top {
          flex-direction: column;
        }

        .menu-item-card.top .item-image {
          width: 100%;
          height: 160px;
        }

        .menu-item-card.bottom {
          flex-direction: column-reverse;
        }

        .menu-item-card.bottom .item-image {
          width: 100%;
          height: 160px;
        }

        .menu-item-card.no-image {
          flex-direction: row;
        }

        .item-image-left {
          width: 100px;
          height: 100px;
          flex-shrink: 0;
          overflow: hidden;
          background: #f5f5f5;
        }

        .item-image-right {
          width: 100px;
          height: 100px;
          flex-shrink: 0;
          overflow: hidden;
          background: #f5f5f5;
        }

        .item-image-top {
          width: 100%;
          height: 160px;
          overflow: hidden;
          background: #f5f5f5;
        }

        .item-image-bottom {
          width: 100%;
          height: 160px;
          overflow: hidden;
          background: #f5f5f5;
        }

        /* Menu Item Detail Overlay */
        .menu-item-overlay {
          position: fixed;
          inset: 0;
          z-index: 1000;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
        }

        .overlay-backdrop {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          animation: fadeIn 0.3s ease;
        }

        .overlay-panel {
          position: relative;
          background: white;
          border-radius: 16px 16px 0 0;
          max-height: 85vh;
          overflow-y: auto;
          animation: slideUp 0.3s ease;
          box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.1);
        }

        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .overlay-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: rgba(255, 255, 255, 0.9);
          border: none;
          border-radius: 50%;
          width: 36px;
          height: 36px;
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          transition: all 0.2s;
          color: var(--pm-text);
        }

        .overlay-close:hover {
          background: white;
          transform: scale(1.1);
        }

        .overlay-image {
          width: 100%;
          height: 300px;
          overflow: hidden;
          background: var(--pm-border);
        }

        .overlay-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .overlay-content {
          padding: 24px;
          background: var(--pm-surface);
        }

        .overlay-title {
          font-size: 24px;
          font-weight: var(--pm-heading-weight);
          color: var(--pm-text);
          margin: 0 0 12px 0;
          font-family: var(--pm-heading-font);
        }

        .overlay-price {
          font-size: 20px;
          font-weight: 700;
          color: var(--pm-price-badge);
          margin: 0 0 16px 0;
          font-family: var(--pm-heading-font);
        }

        .overlay-description {
          font-size: 15px;
          color: var(--pm-text-muted);
          line-height: 1.6;
          margin: 0 0 24px 0;
          font-family: var(--pm-body-font);
        }

        /* Modifier Groups in Overlay */
        .overlay-modifiers {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 24px;
          padding-top: 16px;
          border-top: 1px solid var(--pm-border);
        }

        .modifier-group {
          background: var(--pm-background);
          border-radius: var(--pm-card-radius);
          padding: 12px;
        }

        .modifier-group-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 10px;
          flex-wrap: wrap;
        }

        .modifier-group-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--pm-text);
          margin: 0;
          font-family: var(--pm-heading-font);
        }

        .modifier-group-rule {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }

        .required-tag {
          font-size: 11px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 12px;
          background: #fee2e2;
          color: #dc2626;
        }

        .optional-tag {
          font-size: 11px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 12px;
          background: #dcfce7;
          color: #166534;
        }

        .selection-info {
          font-size: 12px;
          color: var(--pm-text-muted);
        }

        .modifier-options {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .modifier-option {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 10px;
          background: var(--pm-surface);
          border-radius: 8px;
          font-size: 14px;
        }

        .option-name {
          color: var(--pm-text);
          font-family: var(--pm-body-font);
        }

        .option-price {
          font-weight: 600;
          font-size: 13px;
          font-family: var(--pm-body-font);
        }

        .option-price.positive {
          color: #10b981;
        }

        .option-price.negative {
          color: #ef4444;
        }

        .option-final-price {
          font-weight: 700;
          font-size: 14px;
          color: var(--pm-price-badge);
          font-family: var(--pm-heading-font);
        }

        .modifier-group.size-type {
          background: linear-gradient(135deg, var(--pm-background), var(--pm-surface));
          border: 1px solid var(--pm-border);
        }

        .modifier-group.size-type .modifier-option {
          border-left: 3px solid var(--pm-primary);
        }

        .overlay-close-btn {
          width: 100%;
          padding: 12px 20px;
          background: var(--pm-primary);
          border: none;
          border-radius: var(--pm-button-radius);
          color: var(--pm-header-text);
          font-weight: 600;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.2s;
          font-family: var(--pm-body-font);
        }

        .overlay-close-btn:hover {
          opacity: 0.9;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        @media (min-width: 768px) {
          .items-list {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
          }

          .item-image {
            width: 120px;
            height: 120px;
          }
        }

        @media (min-width: 1024px) {
          .items-list {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .menu-footer {
          background: linear-gradient(135deg, var(--pm-secondary), var(--pm-primary));
          color: var(--pm-header-text);
          padding: 40px 20px;
          text-align: center;
          border-top: 3px solid var(--pm-secondary);
        }

        .footer-content {
          max-width: 600px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }

        .footer-link {
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          transition: transform 0.3s ease, opacity 0.3s ease;
        }

        .footer-link:hover {
          transform: translateY(-3px);
          opacity: 0.9;
        }

        .footer-logo {
          height: 40px;
          width: auto;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
        }

        .footer-copyright {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.75);
          margin: 0;
          font-family: var(--pm-body-font);
        }

        .footer-studio-mark {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.45);
          text-decoration: none;
          transition: color 0.2s ease;
          font-family: var(--pm-body-font);
          margin-top: 4px;
        }

        .footer-studio-mark:hover {
          color: rgba(255, 255, 255, 0.7);
        }

        .footer-studio-mark:focus-visible {
          outline: 2px solid rgba(168, 85, 247, 0.7);
          outline-offset: 2px;
          border-radius: 2px;
        }

        @media (min-width: 768px) {
          .menu-footer {
            padding: 50px 20px;
          }

          .footer-logo {
            height: 48px;
          }

          .footer-text {
            font-size: 17px;
          }
        }
      `}</style>
    </div>
    </>
  );
};

export default PublicMenu;
